import { useRef } from 'react'

import { IconContainer } from '@/components/IconContainer'
import { Avatar } from '@/components/Avatar'
import { Close } from '@/components/Close'
import { Input } from '@/components/Input'
import { Button } from '@/components/Button'
import { ErrorMsg } from '@/components/ErrorMsg'
import { useShareLink } from '@/hooks/useShareLink'
import { useForm, ValidationRules } from '@/hooks/useForm'
import { useSharedWith } from '@/hooks/useSharedWith'
import { useRemoveShare } from '@/hooks/useRemoveShare'
import { emailValidation } from '@/utils/validations'
import { Link } from '@/types'

import s from './ShareLink.module.css'

type Props = {
  link?: Link
}

const validationRules: ValidationRules = {
  email: emailValidation,
}

export const ShareLink = ({ link }: Props) => {
  const { data: sharedWithUsers } = useSharedWith(link?.id)
  const shareLinkMutation = useShareLink()
  const removeShareMutation = useRemoveShare()

  const formRef = useRef<HTMLFormElement>(null)
  const { values, errors, handleChange, handleSubmit } = useForm({
    initialValues: {
      email: '',
    } as { email: string },
    validationRules: validationRules,
    formRef: formRef,
  })

  const shareLink = async () => {
    try {
      await shareLinkMutation.mutateAsync({
        linkId: link!.id,
        emails: [values.email],
      })
      formRef.current?.reset()
    } catch {}
  }

  const handleRemove = async (userId: string) => {
    removeShareMutation.mutateAsync({ linkId: link!.id, userId })
  }

  return (
    <div>
      <form
        ref={formRef}
        className={s.form}
        onSubmit={handleSubmit(() => shareLink())}
        noValidate
      >
        <Input
          name="email"
          type="email"
          placeholder="Email address"
          onChange={handleChange}
          error={errors.email}
          autoFocus
          fullWidth
        />
        <Button
          variant="primary"
          type="submit"
          disabled={shareLinkMutation.isPending}
        >
          Share
        </Button>
      </form>

      {errors.email && errors.email !== '-' && <ErrorMsg text={errors.email} />}
      {shareLinkMutation.error?.response?.data.message && (
        <ErrorMsg text={shareLinkMutation.error.response.data.message} />
      )}

      {sharedWithUsers?.length ? (
        <div className={s.users}>
          <b>Shared With</b>
          {sharedWithUsers.map((user) => (
            <div className={s.item} key={user.id}>
              <div className={s.user}>
                <IconContainer size="lg">
                  <Avatar src={user.image} name={user.name} />
                </IconContainer>
                <span className={s.text}>
                  <b>{user.name}</b>
                  <div className={s.email}>{user.email}</div>
                </span>
                <Close
                  onClick={() => handleRemove(user.id)}
                  disabled={removeShareMutation.isPending}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
